import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QrCode } from '@phosphor-icons/react';
import { getJson, isOfflineError } from '../api/client.ts';
import { meQuery, useMachineSearch } from '../api/queries.ts';
import type { MachineDetail } from '../api/types.ts';
import { MachineLightBadge } from '../components/badges.tsx';
import { DuplicateHint } from '../components/duplicate-hint.tsx';
import { NEXT_STEP_LINK, ReportDone, ReportForm, type ReportResult } from '../components/report-form.tsx';
import { Checkbox, ErrorNote, Input, Notice, PageTitle, Spinner } from '../components/ui.tsx';

/**
 * `/melden` – angemeldete Meldung (F18). Maschine per Suche oder „ohne Maschine/Gebäude“.
 * Das Meldeziel steht allein in der Adresse: Maschine und „Ohne Maschine“ schließen sich aus,
 * die Herkunft `qr` endet mit dem Wechsel der Maschine, und „Melden“ in der Leiste leert das Ziel.
 * Die Eingabe überlebt das: Das Formular bleibt ohne Ziel verborgen stehen, sonst wären Text und Fotos weg.
 */
export function ReportPage() {
  const search = useSearch({ from: '/app/melden' });
  const navigate = useNavigate();
  const me = useQuery(meQuery);
  const machineId = search.machineId ?? null;
  const withoutMachine = !machineId && Boolean(search.withoutMachine);
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [result, setResult] = useState<ReportResult | null>(null);
  const [offlineFor, setOfflineFor] = useState<string | null>(null);
  const qc = useQueryClient();
  const chooseMachine = (id: string | null) => void navigate({ to: '/melden', search: id ? { machineId: id } : {} });

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  const lookup = useMachineSearch(debounced, { pageSize: 12, enabled: !machineId });
  const machine = useQuery({
    queryKey: ['machine', machineId],
    queryFn: () => getJson<MachineDetail>(`/api/machines/${machineId}`),
    enabled: Boolean(machineId),
  });

  // Die Maschine steht in der Adresse, melden geht also auch ohne ihre Daten. Der Server prüft sie beim Senden.
  // Jeder neue Abruf ohne Daten löscht Pause und Fehlschlag, bei Netzrückkehr wie beim Fokus. Darum beendet nur eine Antwort diesen Zustand:
  // Sonst nähme flackerndes WLAN das Formular weg, und „Ohne Maschine melden“ liefe wieder über das Hauptwerk.
  const answered = Boolean(machine.data) || (machine.isError && !isOfflineError(machine.error));
  const stalled = machine.isPaused || isOfflineError(machine.failureReason);
  const machineOffline = Boolean(machineId) && !answered && (stalled || offlineFor === machineId);

  useEffect(() => {
    setOfflineFor((held) => (answered ? null : stalled ? machineId : held === machineId ? held : null));
  }, [machineId, answered, stalled]);

  if (result) {
    return (
      <div className="mx-auto max-w-[640px] space-y-4">
        <ReportDone
          result={result}
          onAgain={() => {
            setResult(null);
            setQ('');
            setDebounced('');
            chooseMachine(null);
          }}
          extra={(display) =>
            display ? (
              <Link to="/tickets/$number" params={{ number: display }} className={NEXT_STEP_LINK}>
                Meldung ansehen
              </Link>
            ) : null
          }
        />
      </div>
    );
  }

  const listed = lookup.list?.find((m) => m.id === machineId);
  const offlineLabel = listed ? `${listed.name} · ${listed.inventoryNo} · ${listed.areaName}` : 'Wird beim Senden zugeordnet';
  const label = machine.data ? `${machine.data.name} · ${machine.data.inventoryNo} · ${machine.data.area.name}` : machineOffline ? offlineLabel : null;
  const activeMachine = machine.data?.status === 'active' ? machine.data : null;
  const ready = Boolean(activeMachine) || withoutMachine || machineOffline;
  const linkButton = 'min-h-10 cursor-pointer text-[15px] font-medium text-arnold-red underline';
  const otherMachine = (
    <button type="button" className={linkButton} onClick={() => chooseMachine(null)}>
      Andere Maschine wählen
    </button>
  );
  const leaveMachine = (
    <div className="flex flex-wrap gap-x-5">
      {otherMachine}
      <button type="button" className={linkButton} onClick={() => void navigate({ to: '/melden', search: { withoutMachine: true } })}>
        Ohne Maschine melden
      </button>
    </div>
  );

  return (
    <div className="mx-auto max-w-[640px] space-y-5">
      <PageTitle lead="Maschine wählen, Kachel tippen, ein Foto oder einen Satz dazu. Fertig.">Störung melden</PageTitle>

      {!machineId ? (
        <div className="space-y-3">
          <Link to="/scan" className="ad-button-secondary ad-button-lg w-full no-underline">
            <QrCode size={26} />
            QR-Code scannen
          </Link>
          <div className="flex items-center gap-3 py-1 text-[14px] text-[var(--text-muted)]" aria-hidden>
            <span className="h-px flex-1 bg-[var(--border-default)]" />
            oder suchen
            <span className="h-px flex-1 bg-[var(--border-default)]" />
          </div>
          <Input label="Maschine suchen" value={q} onChange={setQ} placeholder="Name oder Inventarnummer" inputMode="search" />
          {lookup.searching ? <Spinner lines={2} label="Maschinen werden gesucht …" /> : null}
          {lookup.note ? <Notice tone="warn">{lookup.note}</Notice> : null}
          {lookup.items.length ? (
            <ul className="divide-y divide-[var(--border-default)] border border-[var(--border-default)] bg-white">
              {lookup.items.map((m) => (
                <li key={m.id}>
                  <button type="button" onClick={() => chooseMachine(m.id)} className="flex min-h-14 w-full cursor-pointer items-center gap-3 px-4 py-3 text-left hover:bg-steel-50">
                    <span className="min-w-0 flex-1">
                      <span className="block font-semibold text-steel-900">{m.name}</span>
                      <span className="block text-[14px] text-[var(--text-muted)]">
                        <span className="font-mono text-steel-700">{m.inventoryNo}</span>, {m.siteCode}, {m.areaName}
                      </span>
                    </span>
                    {m.light ? <MachineLightBadge light={m.light} /> : null}
                  </button>
                </li>
              ))}
            </ul>
          ) : lookup.results.isError && !lookup.offline ? (
            <ErrorNote error={lookup.results.error} />
          ) : lookup.results.data && !lookup.searching ? (
            <p className="text-[15px] text-[var(--text-muted)]">Keine Maschine gefunden. Anders schreiben oder „ohne Maschine“ wählen.</p>
          ) : null}
          <Checkbox
            label="Ohne Maschine: Gebäude, Tor, Licht, Heizung"
            checked={withoutMachine}
            onChange={(checked) => void navigate({ to: '/melden', search: checked ? { withoutMachine: true } : {}, replace: true, resetScroll: false })}
          />
        </div>
      ) : machine.data ? (
        <>
          <div className="flex items-center justify-between gap-3">
            <MachineLightBadge light={machine.data.light} large />
            {otherMachine}
          </div>
          {activeMachine ? null : <Notice tone="warn">Diese Maschine ist stillgelegt. Bitte eine andere Maschine wählen oder die Instandhaltung direkt ansprechen.</Notice>}
        </>
      ) : machineOffline ? (
        <>
          <Notice tone="warn">Kein Netz. Sicherheitshinweis und offene Meldungen zu dieser Maschine erscheinen erst mit Verbindung. Melden geht trotzdem.</Notice>
          {leaveMachine}
        </>
      ) : (
        <>
          {machine.isError ? <ErrorNote error={machine.error} /> : <Spinner />}
          {leaveMachine}
        </>
      )}

      {machine.data?.safetyNote ? (
        <div className="border-l-4 border-arnold-red bg-arnold-red-tint px-4 py-3 text-[15px] text-steel-900">
          <strong>Sicherheitshinweis:</strong> {machine.data.safetyNote}
        </div>
      ) : null}

      {activeMachine ? <DuplicateHint tickets={activeMachine.tickets} code={activeMachine.qrCode} anonymous={false} /> : null}

      {me.data ? (
        <div hidden={!ready}>
          <ReportForm
            target={{ kind: 'web', machineId, withoutMachine, origin: machineId && search.via === 'qr' ? 'qr' : 'web', owner: me.data.user }}
            anonymous={false}
            machineLabel={withoutMachine ? 'ohne Maschine / Gebäude' : label}
            onDone={(r) => {
              setResult(r);
              void qc.invalidateQueries({ queryKey: ['tickets'] });
            }}
          />
        </div>
      ) : null}
    </div>
  );
}
